import { useLocalizedText } from '../../../../hooks/localizedText';
import Modal from '@amzn/awsui-components-react/polaris/modal';
import Box from '@amzn/awsui-components-react/polaris/box';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Button from '@amzn/awsui-components-react/polaris/button';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import { COORDINATOR_WAITLIST_OPT_OUT_CLEAR_WAITLIST_MODAL } from '../../../../utils/constants';

export interface DisableWaitlistModalProps {
  visible: boolean;
  /** Number of active Amazonians currently on this shift's waitlist. */
  activeWaitlistsCount: number;
  /** When true, blocks confirm and every dismiss path (X, overlay, Escape, Keep button). */
  loading?: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

/**
 * Confirmation for disabling a shift's waitlist when Amazonians are on it (CCE-7137). Confirming
 * flips the shift's `waitlistEnabled` to false in the wizard's local state — it does NOT call any
 * API here. The active entries are removed later, when the coordinator saves the event (removal is
 * deferred to form submit). The optional `loading` prop blocks confirm and all dismiss paths while
 * a caller-supplied operation is in flight.
 */
export function DisableWaitlistModal({
  visible,
  activeWaitlistsCount,
  loading,
  onConfirm,
  onDismiss,
}: DisableWaitlistModalProps) {
  const { t } = useLocalizedText(['events', 'translation']);

  // Guard every dismiss path (X, overlay click, Escape, Keep button): the Modal passes onDismiss
  // through for all of them, so disabling the Keep button alone wouldn't block Escape/overlay.
  const handleDismiss = () => {
    if (!loading) {
      onDismiss();
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={t('shiftStep.disableWaitlistModal.title')}
      closeAriaLabel={t('shiftStep.disableWaitlistModal.closeAriaLabel')}
      data-testid="disable-waitlist-modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={loading}>
              {t('shiftStep.disableWaitlistModal.keepWaitlist')}
            </Button>
            <Button
              variant="primary"
              onClick={onConfirm}
              loading={loading}
              data-aci-analytics-name={COORDINATOR_WAITLIST_OPT_OUT_CLEAR_WAITLIST_MODAL}
            >
              {t('shiftStep.disableWaitlistModal.clearAndDisable')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Alert type="warning">
        {t('shiftStep.disableWaitlistModal.message', { count: activeWaitlistsCount })}
      </Alert>
    </Modal>
  );
}
